import type { SearchHookLocation } from "@repo/maps";
import type { SavedListingsCache, SavedLocationEntry, SavedMapBounds } from "./businesses-view-types";

export const MAP_BOUNDS_STORAGE_KEY = "businesses-map-bounds";
export const LOCATION_STORAGE_KEY = "businesses-last-location";
export const LISTINGS_CACHE_KEY = "businesses-listings-cache";
export const LISTINGS_CACHE_VERSION = 2;
export const LOCATION_TTL_MS = 30 * 60 * 1000; // 30 minutes
export const LISTINGS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes — just enough for a quick back-nav
export const LISTINGS_CACHE_MAX_ITEMS = 100;

export function getSavedMapBounds(): SavedMapBounds | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(MAP_BOUNDS_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SavedMapBounds;
  } catch {
    return null;
  }
}

export function saveMapBounds(bounds: SavedMapBounds) {
  try {
    sessionStorage.setItem(MAP_BOUNDS_STORAGE_KEY, JSON.stringify(bounds));
  } catch {
    // ignore quota errors
  }
}

export function getSavedLocation(): SavedLocationEntry | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(LOCATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SavedLocationEntry;

    if (
      !parsed ||
      typeof parsed !== "object" ||
      !parsed.location ||
      typeof parsed.url !== "string" ||
      typeof parsed.ts !== "number"
    ) {
      sessionStorage.removeItem(LOCATION_STORAGE_KEY);
      return null;
    }

    if (Date.now() - parsed.ts > LOCATION_TTL_MS) {
      sessionStorage.removeItem(LOCATION_STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch {
    try {
      sessionStorage.removeItem(LOCATION_STORAGE_KEY);
    } catch { /* ignore */ }
    return null;
  }
}

export function saveLocation(
  location: SearchHookLocation | null,
  countryCode?: string,
) {
  try {
    if (location) {
      const entry: SavedLocationEntry = {
        location,
        url: window.location.pathname,
        countryCode,
        ts: Date.now(),
      };
      sessionStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(entry));
    } else {
      sessionStorage.removeItem(LOCATION_STORAGE_KEY);
    }
  } catch {
    // ignore quota errors
  }
}

export function saveListingsCache(
  pages: {
    listings: any[];
    nextCursor?: { tier: "PINNED" | "AREA_IN_VIEW" | "WIDER" | "LOCAL" | "AREA"; id: number } | number | null;
  }[],
  count: number,
) {
  try {
    let totalItems = 0;
    const cappedPages: typeof pages = [];
    for (const page of pages) {
      const remaining = LISTINGS_CACHE_MAX_ITEMS - totalItems;
      if (remaining <= 0) break;
      const sliced = page.listings.slice(0, remaining);
      cappedPages.push({
        listings: sliced,
        nextCursor: sliced.length < page.listings.length ? undefined : page.nextCursor,
      });
      totalItems += sliced.length;
    }

    const entry: SavedListingsCache = {
      v: LISTINGS_CACHE_VERSION,
      pages: cappedPages,
      count,
      ts: Date.now(),
    };
    sessionStorage.setItem(LISTINGS_CACHE_KEY, JSON.stringify(entry));
  } catch {
    // sessionStorage full or unavailable — fail silently
  }
}

export function getListingsCache(): SavedListingsCache | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(LISTINGS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SavedListingsCache;

    if (
      !parsed ||
      typeof parsed !== "object" ||
      !Array.isArray(parsed.pages) ||
      typeof parsed.count !== "number" ||
      typeof parsed.ts !== "number" ||
      parsed.v !== LISTINGS_CACHE_VERSION
    ) {
      sessionStorage.removeItem(LISTINGS_CACHE_KEY);
      return null;
    }

    if (Date.now() - parsed.ts > LISTINGS_CACHE_TTL_MS) {
      sessionStorage.removeItem(LISTINGS_CACHE_KEY);
      return null;
    }

    return parsed;
  } catch {
    try {
      sessionStorage.removeItem(LISTINGS_CACHE_KEY);
    } catch { /* ignore */ }
    return null;
  }
}
